'use client';

import Link from 'next/link';
import { Rocket, X } from 'lucide-react';

interface TenantSetupBannerProps {
  onDismiss: () => void;
  /** Number of tenant setup steps that are completed or explicitly resolved */
  resolvedSteps?: number;
  /** Number of completed tenant setup steps (legacy prop name retained for callers during migration) */
  completedSteps?: number;
  /** Number of deferred setup decisions */
  deferredSteps?: number;
  /** Number of setup decisions that block go-live */
  blockedSteps?: number;
  /** Total tenant setup steps */
  totalSteps?: number;
}

export function TenantSetupBanner({
  onDismiss,
  resolvedSteps,
  completedSteps,
  deferredSteps = 0,
  blockedSteps = 0,
  totalSteps,
}: TenantSetupBannerProps) {
  const resolvedCount = resolvedSteps ?? completedSteps;
  const hasProgress = resolvedCount != null && totalSteps != null && resolvedCount > 0;

  return (
    <div
      className={`border-b px-4 py-3 ${
        blockedSteps > 0 ? 'border-red-200 bg-red-50' : 'border-emerald-200 bg-emerald-50'
      }`}
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Rocket
            className={`h-5 w-5 shrink-0 ${blockedSteps > 0 ? 'text-red-600' : 'text-emerald-600'}`}
            aria-hidden="true"
          />
          <p className={`text-body ${blockedSteps > 0 ? 'text-red-800' : 'text-emerald-800'}`}>
            {hasProgress ? (
              <>
                <strong>Organisation setup in progress</strong> — {resolvedCount} of {totalSteps}{' '}
                steps resolved
                {deferredSteps > 0 ? `, ${deferredSteps} deferred` : ''}
                {blockedSteps > 0 ? `, ${blockedSteps} blocking go-live` : ''}.{' '}
                <Link
                  href="/onboarding"
                  className={`font-medium underline ${
                    blockedSteps > 0
                      ? 'text-red-900 hover:text-red-700'
                      : 'text-emerald-900 hover:text-emerald-700'
                  }`}
                >
                  Continue organisation setup
                </Link>
              </>
            ) : (
              <>
                <strong>Organisation setup pending.</strong> Complete organisation setup before
                go-live.{' '}
                <Link
                  href="/onboarding"
                  className={`font-medium underline ${
                    blockedSteps > 0
                      ? 'text-red-900 hover:text-red-700'
                      : 'text-emerald-900 hover:text-emerald-700'
                  }`}
                >
                  Start organisation setup
                </Link>
              </>
            )}
          </p>
        </div>
        <button
          onClick={onDismiss}
          className={`shrink-0 rounded-control p-1 transition-colors ${
            blockedSteps > 0
              ? 'text-red-500 hover:bg-red-100 hover:text-red-700'
              : 'text-emerald-500 hover:bg-emerald-100 hover:text-emerald-700'
          }`}
          aria-label="Dismiss organisation setup banner"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
